import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";

// This panel verifies SAVED Novel data and captures a separate private archive.
// It never locks Canon, approves an interior, prints or publishes.
const clean = (value) => String(value ?? "").trim();
const snapshotBase = (storyId) =>
  "/api/stories/" + encodeURIComponent(storyId) + "/production-source";
const displayDate = (value) => {
  if (!value) return "";
  const time = new Date(value);
  return Number.isNaN(time.getTime()) ? "" : time.toLocaleString();
};

export function novelSnapshotCaptureEligible({
  status = null, verifiedFingerprint = "", busy = "", previewOnly = false,
} = {}) {
  return Boolean(!busy && !previewOnly && status?.captureEnabled === true &&
    status?.sourceReady === true && /^[a-f0-9]{64}$/.test(verifiedFingerprint) &&
    verifiedFingerprint === status?.fingerprint);
}

export function novelSnapshotPrivatePdfEligible({
  storyId = "", snapshot = null, snapshotVerified = false,
  captureEnabled = false, busy = "", previewOnly = false,
} = {}) {
  return Boolean(!busy && !previewOnly && captureEnabled && snapshotVerified &&
    /^[a-f0-9]{24}$/i.test(clean(storyId)) &&
    /^[a-f0-9]{24}$/i.test(clean(snapshot?.snapshotId)) &&
    /^[a-f0-9]{64}$/.test(clean(snapshot?.fingerprint)));
}

export default function NovelManuscriptSnapshotVerification({
  storyId = "", previewOnly = false, isFR = false,
}) {
  const label = (en, fr) => isFR ? fr : en;
  const [status, setStatus] = useState(null);
  const [verifiedFingerprint, setVerifiedFingerprint] = useState("");
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotVerified, setSnapshotVerified] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const proofEpoch = useRef(0);
  useEffect(() => () => {
    if (proofUrl) URL.revokeObjectURL(proofUrl);
  }, [proofUrl]);

  useEffect(() => {
    let active = true;
    setStatus(null);
    setVerifiedFingerprint("");
    setSnapshot(null);
    setSnapshotVerified(false);
    proofEpoch.current += 1;
    setProofUrl("");
    setError("");
    setMessage("");
    if (!clean(storyId) || previewOnly) return () => { active = false; };
    api.get(snapshotBase(storyId) + "/status").then((result) => {
      if (!active) return;
      setStatus(result);
      setSnapshot(result?.latestSnapshot || null);
    }).catch((err) => {
      if (active) setError(clean(err?.message) || "Unable to check the saved manuscript.");
    });
    return () => { active = false; };
  }, [storyId, previewOnly]);

  async function verifyManuscript() {
    if (!clean(storyId) || previewOnly || busy) return;
    setBusy("verify");
    setError("");
    setMessage("");
    setVerifiedFingerprint("");
    setSnapshotVerified(false);
    proofEpoch.current += 1;
    setProofUrl("");
    try {
      const result = await api.get(snapshotBase(storyId) + "/status");
      if (result?.ok !== true) throw new Error("Saved manuscript verification failed.");
      setStatus(result);
      setSnapshot(result.latestSnapshot || null);
      if (result.sourceReady === true && /^[a-f0-9]{64}$/.test(clean(result.fingerprint))) {
        setVerifiedFingerprint(result.fingerprint);
        setMessage(label(
          "Saved manuscript verified. This is source verification, not publication approval.",
          "Manuscrit enregistré vérifié. Il ne s'agit pas d'une approbation de publication."
        ));
      } else {
        setMessage(label(
          "The saved manuscript has blocking issues. Resolve them and verify again.",
          "Le manuscrit enregistré présente des anomalies. Corrigez-les et revérifiez."
        ));
      }
    } catch (err) {
      setError(clean(err?.message) || "Saved manuscript verification failed.");
    } finally {
      setBusy("");
    }
  }

  async function captureSnapshot() {
    if (!clean(storyId) || !novelSnapshotCaptureEligible({
      status, verifiedFingerprint, busy, previewOnly,
    })) return;
    setBusy("capture");
    setError("");
    setMessage("");
    setSnapshotVerified(false);
    proofEpoch.current += 1;
    setProofUrl("");
    try {
      const result = await api.post(snapshotBase(storyId) + "/snapshots", {
        expectedFingerprint: verifiedFingerprint,
      });
      if (result?.ok !== true || !clean(result?.snapshotId) ||
          result.fingerprint !== verifiedFingerprint) {
        throw new Error("The saved snapshot identity did not match the verified manuscript.");
      }
      setSnapshot({
        snapshotId: result.snapshotId,
        fingerprint: result.fingerprint,
        createdAt: result.createdAt || null,
      });
      setVerifiedFingerprint("");
      setMessage(label(
        "Production source captured. Verify the saved snapshot before continuing.",
        "Source de production capturée. Vérifiez la copie enregistrée avant de continuer."
      ));
    } catch (err) {
      setVerifiedFingerprint("");
      setError(clean(err?.message) || "Snapshot capture failed. Verify the manuscript again.");
    } finally {
      setBusy("");
    }
  }

  async function verifySavedSnapshot() {
    const snapshotId = clean(snapshot?.snapshotId);
    if (busy || previewOnly || !clean(storyId) || !snapshotId) return;
    setBusy("snapshot");
    setError("");
    setMessage("");
    setSnapshotVerified(false);
    proofEpoch.current += 1;
    setProofUrl("");
    try {
      const result = await api.get(snapshotBase(storyId) + "/snapshots/" +
        encodeURIComponent(snapshotId));
      if (result?.ok !== true || result.snapshotId !== snapshotId ||
          result.fingerprint !== snapshot?.fingerprint ||
          result.status !== "source-captured-only" ||
          result.manuscriptLocked !== false || result.printPdfReady !== false) {
        throw new Error("The saved snapshot could not be matched and verified.");
      }
      setSnapshot({
        snapshotId: result.snapshotId,
        fingerprint: result.fingerprint,
        createdAt: result.createdAt,
        sourceUpdatedAt: result.sourceUpdatedAt,
      });
      setSnapshotVerified(true);
      setMessage(label(
        "Saved snapshot integrity verified. No manuscript lock or print file has been created.",
        "Intégrité de la copie vérifiée. Aucun verrouillage ni fichier d'impression n'a été créé."
      ));
    } catch (err) {
      setError(clean(err?.message) || "Saved snapshot verification failed.");
    } finally {
      setBusy("");
    }
  }

  async function openPrivateSnapshotProof() {
    if (!novelSnapshotPrivatePdfEligible({
      storyId, snapshot, snapshotVerified,
      captureEnabled: status?.captureEnabled === true, busy, previewOnly,
    })) return;
    const epoch = ++proofEpoch.current;
    setBusy("proof");
    setError("");
    setMessage("");
    setProofUrl("");
    try {
      const pdf = await api.getNovelPrivateSnapshotMeasuredInteriorProof(
        storyId, snapshot.snapshotId, snapshot.fingerprint
      );
      if (epoch !== proofEpoch.current) return;
      setProofUrl(URL.createObjectURL(pdf));
      setMessage(label(
        "Private PDF prepared from the verified saved snapshot. Provisional pages only — no print approval.",
        "PDF privé issu de la copie vérifiée. Pagination provisoire, sans approbation d'impression."
      ));
    } catch (err) {
      if (epoch !== proofEpoch.current) return;
      setError(clean(err?.message) || label(
        "Could not open the private snapshot interior PDF.",
        "Impossible d'ouvrir le PDF privé de la copie."
      ));
    } finally {
      if (epoch === proofEpoch.current) setBusy("");
    }
  }

  const ready = status?.sourceReady === true;
  const captureEnabled = status?.captureEnabled === true;
  const canCapture = Boolean(clean(storyId) && novelSnapshotCaptureEligible({
    status, verifiedFingerprint, busy, previewOnly,
  }));
  const canPreviewArchive = novelSnapshotPrivatePdfEligible({
    storyId, snapshot, snapshotVerified, captureEnabled, busy, previewOnly,
  });
  const latestDiffers = Boolean(snapshot?.fingerprint && status?.fingerprint &&
    snapshot.fingerprint !== status.fingerprint);

  return (
    <article className="ns-card" data-novel-manuscript-snapshot-verification="v1"
      style={{ padding: 20, borderRadius: 18, background: "#f5fbf7",
        border: "1px solid #bee3c9", borderLeft: "5px solid #15803d" }}>
      <h3 style={{ margin: "0 0 7px", display: "flex", alignItems: "center",
        gap: 10, color: "#166534" }}>
        <span aria-hidden="true" style={{ display: "inline-grid", placeItems: "center",
          flex: "0 0 32px", height: 32, borderRadius: 10,
          background: "#d9f5e3", color: "#166534", fontSize: 13, fontWeight: 850 }}>02</span>
        {label("Manuscript Snapshot Verification", "Vérification de la copie du manuscrit")}
      </h3>
      <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
        {label(
          "Verify the current saved book, capture an exact owner-only production-source copy, then confirm its stored identity. This never locks, approves or publishes the manuscript.",
          "Vérifiez le livre enregistré, conservez une copie privée pour la production, puis contrôlez son identité. Aucune publication ni approbation."
        )}
      </p>
      <div data-novel-snapshot-capture-gate={captureEnabled ? "enabled" : "disabled"}
        style={{ padding: "10px 12px", background: "#f8fafc", borderRadius: 10 }}>
        <strong>{captureEnabled
          ? label("Snapshot capture available", "Capture de la copie disponible")
          : label("Snapshot capture not enabled on this server", "Capture non activée sur ce serveur")}</strong>
        <div className="ns-card-meta" style={{ marginTop: 4 }}>
          {status
            ? (ready
              ? label("Saved source ready", "Source enregistrée prête") + " · " +
                status.sectionCount + " " + label("sections", "sections") + " · " +
                status.assignedCanonScenes + " Canon Scenes"
              : label("Saved source requires attention", "La source doit être corrigée"))
            : label("Source check pending or unavailable", "Contrôle en attente ou indisponible")}
        </div>
        {Array.isArray(status?.issues) && status.issues.length > 0 ? (
          <p role="alert" className="ns-card-meta" style={{ margin: "7px 0 0", color: "#92400e" }}>
            {label("Blocking checks", "Anomalies")} : {status.issues.join(" · ")}
          </p>
        ) : null}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
        <button type="button" className="ns-button ns-button-outline"
          data-novel-snapshot-verify-manuscript="true"
          disabled={!clean(storyId) || previewOnly || Boolean(busy)}
          onClick={verifyManuscript}>
          {busy === "verify" ? label("Verifying…", "Vérification…") :
            label("1. Verify Saved Manuscript", "1. Vérifier le manuscrit")}
        </button>
        <button type="button" className="ns-button ns-button-primary"
          data-novel-snapshot-capture="true" disabled={!canCapture}
          onClick={captureSnapshot}>
          {busy === "capture" ? label("Capturing…", "Capture…") :
            label("2. Capture Production Snapshot", "2. Capturer une copie de production")}
        </button>
        <button type="button" className="ns-button ns-button-outline"
          data-novel-snapshot-verify-saved="true"
          disabled={!clean(snapshot?.snapshotId) || !captureEnabled || Boolean(busy) || previewOnly}
          onClick={verifySavedSnapshot}>
          {busy === "snapshot" ? label("Checking…", "Contrôle…") :
            label("3. Verify Saved Snapshot", "3. Vérifier la copie enregistrée")}
        </button>
      </div>

      <div data-novel-snapshot-status="true" className="ns-card-meta" style={{ marginTop: 12 }}>
        {snapshot?.snapshotId ? (
          <>
            <div><strong>{label("Saved snapshot ID", "Identifiant de la copie")}:</strong> {snapshot.snapshotId}</div>
            <div><strong>{label("Captured", "Capturée")}:</strong> {displayDate(snapshot.createdAt) ||
              label("Not yet confirmed", "Non encore confirmé")}</div>
            <div><strong>{label("Integrity", "Intégrité")}:</strong> {
              snapshotVerified
                ? label("Verified", "Vérifiée")
                : label("Not verified in this session", "Non vérifiée dans cette session")
            }</div>
            {latestDiffers ? (
              <p style={{ color: "#92400e" }}>
                {label("The saved manuscript has changed since this snapshot; verify and capture a new source when needed.",
                  "Le manuscrit a changé depuis cette copie ; vérifiez et capturez la nouvelle source.")}
              </p>
            ) : null}
          </>
        ) : label("No saved production snapshot is recorded for this book.", "Aucune copie de production enregistrée.")}
      </div>
      <div data-novel-snapshot-private-pdf="owner-only" style={{ marginTop: 14,
        padding: "12px 0 0", borderTop: "1px solid #bee3c9" }}>
        <button type="button" className="ns-button ns-button-outline"
          data-novel-snapshot-private-pdf-open="true"
          disabled={!canPreviewArchive}
          onClick={openPrivateSnapshotProof}>
          {busy === "proof" ? label("Preparing Private PDF…", "Préparation du PDF privé…") :
            label("4. Preview Verified Snapshot Interior PDF", "4. Aperçu PDF de la copie vérifiée")}
        </button>
        <p className="ns-card-meta" style={{ margin: "8px 0 0" }}>
          {label(
            "Available after verifying the saved snapshot. Private, measured and provisional; uses the archived source, not the current editable manuscript. No final export or approval.",
            "Disponible après vérification de la copie. Aperçu privé et provisoire de l'archive, sans export final ni approbation."
          )}
        </p>
        {proofUrl ? (
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center",
              justifyContent: "space-between", flexWrap: "wrap" }}>
              <strong>{label("Verified snapshot · private provisional PDF", "Copie vérifiée · PDF provisoire privé")}</strong>
              <button type="button" className="ns-button ns-button-outline"
                data-novel-snapshot-private-pdf-close="true" onClick={() => {
                  proofEpoch.current += 1;
                  setProofUrl("");
                  setMessage("");
                }}>{label("Close PDF Preview", "Fermer l'aperçu PDF")}</button>
            </div>
            <iframe title={label("Private verified snapshot interior PDF", "PDF intérieur privé vérifié")}
              data-novel-snapshot-private-pdf-frame="true"
              src={proofUrl} style={{ width: "100%", height: "min(75vh, 900px)",
                minHeight: 390, border: "1px solid #cbd5e1", borderRadius: 10 }}/>
          </div>
        ) : null}
      </div>
      {message ? <p role="status" style={{ margin: "10px 0 0", color: "#047857" }}>{message}</p> : null}
      {error ? <p role="alert" style={{ margin: "10px 0 0", color: "#b91c1c" }}>{error}</p> : null}
      <p className="ns-card-meta" style={{ margin: "10px 0 0" }}>
        {label(
          "Source snapshot only — no manuscript lock, final page count, PDF, cover geometry, approval or export.",
          "Copie de source uniquement — aucun verrouillage, PDF final, géométrie de couverture, approbation ou export."
        )}
      </p>
    </article>
  );
}
